import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowUpRight, Package, Truck, Building2, Recycle } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ListingCard from "@/components/ListingCard";

const HERO_IMG = "https://images.unsplash.com/photo-1572521165329-b197f9ea3da6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBvZmZpY2UlMjB3b3Jrc3BhY2UlMjBmdXJuaXR1cmV8ZW58MHx8fHwxNzg0MjAwMDk3fDA&ixlib=rb-4.1.0&q=85";
const OFFICE_IMG = "https://images.unsplash.com/photo-1718220216044-006f43e3a9b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBvZmZpY2UlMjB3b3Jrc3BhY2UlMjBmdXJuaXR1cmV8ZW58MHx8fHwxNzg0MjAwMDk3fDA&ixlib=rb-4.1.0&q=85";
const CHAIRS_IMG = "https://images.unsplash.com/photo-1579487785973-74d2ca7abdd5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjB3b3Jrc3BhY2UlMjBmdXJuaXR1cmV8ZW58MHx8fHwxNzg0MjAwMDk3fDA&ixlib=rb-4.1.0&q=85";
const REMOVALS_IMG = "https://images.unsplash.com/photo-1620455800201-7f00aeef12ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxtb3ZpbmclMjB2YW4lMjBsb2FkaW5nJTIwYm94ZXN8ZW58MHx8fHwxNzg0MjAwMDk4fDA&ixlib=rb-4.1.0&q=85";
const TABLE_IMG = "https://images.unsplash.com/photo-1431540015161-0bf868a2d407?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxvZmZpY2UlMjBtZWV0aW5nJTIwcm9vbSUyMHRhYmxlfGVufDB8fHx8MTc4NDIwMDA5OHww&ixlib=rb-4.1.0&q=85";

const CATEGORIES = [
  { name: "Desks", img: OFFICE_IMG },
  { name: "Chairs & Seating", img: CHAIRS_IMG },
  { name: "Meeting Tables", img: TABLE_IMG },
  { name: "Whole Office Clearance", img: OFFICE_IMG },
];

export default function Home() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("");
  const [listings, setListings] = useState([]);
  const [stats, setStats] = useState(null);
  const [displayStats, setDisplayStats] = useState({ companies: 0, live_listings: 0, furniture_rehomed: 0, diverted_percent: 0 });

  useEffect(() => {
    api.get("/listings?limit=8")
      .then(({ data }) => setListings(data.items || []))
      .catch(() => {});
    api.get("/stats/public")
      .then(({ data }) => setStats(data))
      .catch(() => {});
  }, []);

  // Count-up animation once stats arrive
  useEffect(() => {
    if (!stats) return;
    const duration = 1200;
    const start = performance.now();
    const targets = {
      companies: stats.companies,
      live_listings: stats.live_listings,
      furniture_rehomed: stats.furniture_rehomed,
      diverted_percent: stats.diverted_percent,
    };
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplayStats({
        companies: Math.round(targets.companies * eased),
        live_listings: Math.round(targets.live_listings * eased),
        furniture_rehomed: Math.round(targets.furniture_rehomed * eased),
        diverted_percent: (targets.diverted_percent * eased),
      });
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [stats]);

  const fmt = (n) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000)     return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
    return `${n}`;
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (location) p.set("location", location);
    navigate(`/listings?${p.toString()}`);
  };

  return (
    <div>
      {/* HERO — Tetris grid */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pt-10 lg:pt-14">
        <div className="grid grid-cols-12 gap-4 lg:gap-6">
          {/* Big hero copy */}
          <div className="col-span-12 lg:col-span-7 border border-border bg-white p-8 lg:p-12 relative overflow-hidden">
            <div className="overline text-safety">The UK office furniture exchange</div>
            <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter text-forest">
              Buy. Sell. Clear.<br/>
              <span className="font-light">Every desk, chair &<br/>whole office in Britain.</span>
            </h1>
            <p className="mt-6 text-base text-muted-foreground max-w-md leading-relaxed">
              A no-nonsense marketplace connecting businesses, buyers and commercial removal firms —
              so nothing good ends up in landfill.
            </p>

            <form onSubmit={submitSearch} className="mt-8 grid grid-cols-1 md:grid-cols-[1fr,220px,auto] gap-2 border border-forest bg-white">
              <div className="flex items-center px-4 gap-3 border-b md:border-b-0 md:border-r border-border">
                <Search className="w-4 h-4 text-muted-foreground shrink-0" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search desks, Herman Miller, whole clearance…"
                  data-testid="hero-search-q"
                  className="border-0 focus-visible:ring-0 shadow-none rounded-none h-12"
                />
              </div>
              <Input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Location or postcode"
                data-testid="hero-search-location"
                className="border-0 focus-visible:ring-0 shadow-none rounded-none h-12 px-4"
              />
              <Button
                type="submit"
                data-testid="hero-search-submit"
                className="rounded-none bg-forest hover:bg-forest-800 text-white h-12 px-8"
              >
                Search
              </Button>
            </form>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-6 pt-6 border-t border-border" data-testid="hero-stats">
              {[
                { n: fmt(displayStats.live_listings), l: "Live listings", tid: "stat-listings" },
                { n: fmt(displayStats.companies), l: "Removal firms", tid: "stat-companies" },
                { n: fmt(displayStats.furniture_rehomed), l: "Furniture rehomed", tid: "stat-rehomed" },
                { n: `${displayStats.diverted_percent.toFixed(0)}%`, l: "Diverted from landfill", tid: "stat-diverted" },
              ].map((s) => (
                <div key={s.l}>
                  <div data-testid={s.tid} className="font-display font-black text-2xl text-forest tabular-nums">{s.n}</div>
                  <div className="overline text-muted-foreground mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero image */}
          <div className="col-span-12 lg:col-span-5 grid grid-rows-2 gap-4 lg:gap-6">
            <div className="relative overflow-hidden border border-border grain-overlay row-span-2">
              <img src={HERO_IMG} alt="Modern office workspace" className="w-full h-full object-cover min-h-[280px]" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-forest/85 via-forest/30 to-transparent p-6">
                <div className="overline text-white/80">Our mission</div>
                <div className="mt-1 font-display font-black text-2xl text-white tracking-tight">Keeping furniture out of landfill</div>
                <div className="text-sm text-white/70 mt-1">Our aim: every desk, chair and cabinet reused, recycled or upcycled — never landfill</div>
                <Link
                to="/about"
                data-testid="hero-clearance-link"
                className="mt-4 inline-flex items-center gap-1 text-white overline group"
              >
                About us <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY GRID */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 mt-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="overline text-safety">Categories</div>
            <h2 className="mt-2 font-display font-light text-3xl lg:text-4xl tracking-tight text-forest">Browse by what you need</h2>
          </div>
          <Link to="/listings" className="hidden md:inline-flex items-center gap-1 text-sm text-forest hover:text-safety transition-colors duration-200">
            All categories <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {CATEGORIES.map((c) => (
            <Link
              key={c.name}
              to={`/listings?category=${encodeURIComponent(c.name)}`}
              data-testid={`category-tile-${c.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className="group relative overflow-hidden border border-border aspect-[4/5] block"
            >
              <img src={c.img} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-forest/85"></div>
              <div className="relative h-full flex flex-col justify-end p-5 text-white">
                <div className="overline text-white/70">Category</div>
                <div className="font-display font-medium text-xl tracking-tight mt-1">{c.name}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* THREE USER TYPES */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 mt-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-border">
          {[
            { icon: Building2, title: "For offices closing down", body: "List a single desk or your entire floor. Free to post, feature it for reach.", cta: "Post a listing", to: "/sell", tid: "usercard-offices" },
            { icon: Truck, title: "For removal companies", body: "Get seen by businesses closing offices. Publish your service areas & get quote requests.", cta: "List your company", to: "/register?type=removal", tid: "usercard-removal" },
            { icon: Package, title: "For buyers", body: "Individuals and small firms — score high-quality office kit at a fraction of retail.", cta: "Browse now", to: "/listings", tid: "usercard-buyers" },
          ].map((u, i) => (
            <div key={u.title} className={`p-8 lg:p-10 bg-white ${i < 2 ? "md:border-r border-border" : ""} ${i > 0 ? "border-t md:border-t-0 border-border" : ""}`}>
              <div className="w-12 h-12 bg-forest text-white grid place-items-center">
                <u.icon className="w-5 h-5" />
              </div>
              <div className="mt-6 font-display text-xl tracking-tight">{u.title}</div>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{u.body}</p>
              <Link to={u.to} data-testid={u.tid} className="mt-6 inline-flex items-center gap-1 text-safety overline group">
                {u.cta} <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* LATEST LISTINGS */}
      {listings.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 lg:px-10 mt-24">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="overline text-safety">Fresh from the floor</div>
              <h2 className="mt-2 font-display font-light text-3xl lg:text-4xl tracking-tight text-forest">Latest listings</h2>
            </div>
            <Link to="/listings" className="hidden md:inline-flex items-center gap-1 text-sm text-forest hover:text-safety transition-colors duration-200">
              Browse all <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {listings.map((l) => <ListingCard key={l.listing_id} listing={l} />)}
          </div>
        </section>
      )}

      {/* REMOVALS */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 mt-24">
        <div className="grid grid-cols-12 gap-4 lg:gap-6">
          <div className="col-span-12 lg:col-span-5 relative overflow-hidden border border-border min-h-[320px]">
            <img src={REMOVALS_IMG} alt="Removal company loading boxes" className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="col-span-12 lg:col-span-7 border border-border bg-white p-8 lg:p-12">
            <div className="overline text-safety">Removal directory</div>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-5xl tracking-tighter text-forest leading-none">
              Need it moved,<br/>recycled or gone?
            </h2>
            <p className="mt-6 text-base text-muted-foreground max-w-md leading-relaxed">
              Browse vetted commercial removal firms across the UK — from same-day office
              clearance to certified IT & furniture recycling.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild data-testid="find-mover-btn" className="rounded-none bg-forest hover:bg-forest-800 text-white h-12 px-8">
                <Link to="/removal-companies">Find a mover</Link>
              </Button>
              <Button asChild variant="outline" data-testid="list-your-company-btn" className="rounded-none border-forest text-forest hover:bg-forest hover:text-white h-12 px-8">
                <Link to="/register?type=removal">List your company</Link>
              </Button>
            </div>
            <div className="mt-8 pt-6 border-t border-border flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><Recycle className="w-4 h-4 text-safety" /> Certified recycling partners</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 mt-24">
        <div className="border border-border bg-forest text-white p-10 lg:p-16 grain-overlay">
          <div className="max-w-3xl">
            <div className="overline text-safety">Post free · takes 2 minutes</div>
            <h2 className="mt-4 font-display font-black text-4xl lg:text-6xl leading-none tracking-tighter">
              Turn your redundant<br/>office furniture into cash.
            </h2>
            <p className="mt-6 text-white/70 max-w-md leading-relaxed">
              Post a listing in under two minutes. Feature it from £9.99 to reach thousands
              of UK buyers and clearance-hungry businesses.
            </p>
            <Button
              asChild
              data-testid="cta-post-listing"
              className="mt-8 rounded-none bg-safety hover:bg-safety-hover text-white h-14 px-10 text-base"
            >
              <Link to="/sell">Post a free listing →</Link>
            </Button>
          </div>
        </div>
      </section>
      <div className="h-12" />
    </div>
  );
}
